import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Menu, X } from "lucide-react";
import { BRAND, WA_DEFAULT } from "@/data/site";
import { scrollToId } from "@/lib/lenis";

const LINKS = [
  { label: "Destinos", id: "#destinos", testid: "nav-link-destinos" },
  { label: "Processo", id: "#processo", testid: "nav-link-processo" },
  { label: "Depoimentos", id: "#depoimentos", testid: "nav-link-depoimentos" },
  { label: "Dúvidas", id: "#faq", testid: "nav-link-faq" },
  { label: "Contato", id: "#contato", testid: "nav-link-contato" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      data-testid="main-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
        scrolled
          ? "bg-navy-950/85 backdrop-blur-xl border-b border-gold-400/15"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8">
        <button
          data-testid="brand-logo-header"
          onClick={() => go("#inicio")}
          className="flex items-center"
        >
          <img
            src={BRAND.logo}
            alt="Melo Vistos"
            className="h-12 w-12 rounded-lg border border-gold-400/40 object-cover"
          />
        </button>

        <nav className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={l.testid}
              onClick={() => go(l.id)}
              className="group relative text-sm font-light tracking-wide text-slate-300 transition-colors duration-300 hover:text-gold-300"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold-400 transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <span className="font-mono-tech text-[10px] tracking-widest text-slate-500">
            CNPJ {BRAND.cnpj}
          </span>
          <a
            data-testid="header-whatsapp-cta"
            href={WA_DEFAULT}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border border-gold-400/50 bg-gold-400/10 px-5 py-2 text-sm font-medium text-gold-200 transition-colors duration-300 hover:bg-gold-400 hover:text-navy-950"
          >
            <MessageCircle className="h-4 w-4" />
            WhatsApp
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen(!open)}
          className="text-slate-200 lg:hidden"
          aria-label="Menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      {open && (
        <motion.nav
          initial={{ opacity: 0, y: -12 }}
          animate={{ opacity: 1, y: 0 }}
          className="border-t border-gold-400/15 bg-navy-950/95 px-6 py-6 backdrop-blur-xl lg:hidden"
        >
          <div className="flex flex-col gap-5">
            {LINKS.map((l) => (
              <button
                key={l.id}
                data-testid={`${l.testid}-mobile`}
                onClick={() => go(l.id)}
                className="text-left text-base text-slate-200"
              >
                {l.label}
              </button>
            ))}
            <a
              data-testid="header-whatsapp-cta-mobile"
              href={WA_DEFAULT}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 flex w-fit items-center gap-2 rounded-full border border-gold-400/50 bg-gold-400/10 px-5 py-2 text-sm font-medium text-gold-200"
            >
              <MessageCircle className="h-4 w-4" />
              WhatsApp
            </a>
          </div>
        </motion.nav>
      )}
    </motion.header>
  );
};
